import { useCallback, useState } from "react";
import QRCode from "qrcode";
import logo from "../../../assets/logo/Logo For Light theme.png";
import { notify } from "../../UI/DesignStandards/Notifications/notify";
import { SITE_ORIGIN } from "../../SEO/siteOrigin";
import { tournamentPath } from "../../../utility/tournamentUrls";

const PAGE_WIDTH = 595;
const PAGE_HEIGHT = 842;
const MARGINS = { top: 60, right: 40, bottom: 110, left: 40 };
const TABLE_CONFIG = {
  headerHeight: 24,
  rowHeight: 22,
  topOffset: 120,
};

let cachedLogoDataUrl = null;

const slugify = (value = "") =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-") || "tournament";

const formatCurrency = (value) => {
  if (!value || Number.isNaN(Number(value))) return "-";
  return `$${Number(value).toFixed(2)}`;
};

const formatScore = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "number") return value.toLocaleString();
  return value;
};

const stripPlacementRules = (value) => {
  if (typeof value !== "string") return value;
  const cleaned = value
    // Remove "(placement rules)" or "(placement rules )" with parentheses
    .replace(/\s*\([^)]*placement\s+rules[^)]*\)/gi, "")
    // Also remove standalone "placement rules" text
    .replace(/\s*placement\s+rules\s*/gi, "")
    // Clean up multiple spaces
    .replace(/\s{2,}/g, " ")
    // Remove leading/trailing spaces and parentheses that might be left
    .replace(/^\s*[\(\s]+|[\)\s]+\s*$/g, "")
    .trim();
  return cleaned;
};

const compressImage = (dataUrl, maxWidth = 200, quality = 0.7) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const scale = Math.min(maxWidth / img.width, maxWidth / img.height, 1);
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      // Use JPEG for better compression (unless it's already PNG with transparency)
      const isPngWithAlpha = dataUrl.includes("data:image/png");
      const format = isPngWithAlpha ? "image/png" : "image/jpeg";
      resolve(canvas.toDataURL(format, quality));
    };
    img.onerror = () => resolve(dataUrl); // Fallback to original if compression fails
    img.src = dataUrl;
  });
};

const loadImageAsDataUrl = async (src) => {
  if (cachedLogoDataUrl) return cachedLogoDataUrl;
  try {
    const response = await fetch(src);
    const blob = await response.blob();
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    // Compress the logo to reduce payload size
    cachedLogoDataUrl = await compressImage(dataUrl, 150, 0.7);
    return cachedLogoDataUrl;
  } catch (error) {
    console.error("Failed to load logo image:", error);
    return null;
  }
};

const getStringLength = (value) => {
  if (value === null || value === undefined) return 0;
  return value.toString().trim().length || 0;
};

const computeColumnWidths = (columns, rows, totalWidth) => {
  if (!columns.length) return [];

  const analyzed = columns.map((col) => {
    let maxLen = getStringLength(col.label);
    rows.forEach((row) => {
      const value = row[col.key];
      if (Array.isArray(value)) {
        const joined = value.join(", ");
        maxLen = Math.max(maxLen, getStringLength(joined));
      } else {
        maxLen = Math.max(maxLen, getStringLength(value));
      }
    });
    return {
      ...col,
      maxLen: Math.max(maxLen, col.importance ?? 1),
    };
  });

  const minTotal = analyzed.reduce((sum, col) => sum + (col.minWidth || 60), 0);
  const extraSpace = Math.max(0, totalWidth - minTotal);
  const totalWeight =
    analyzed.reduce((sum, col) => sum + (col.maxLen || 1), 0) || 1;

  return analyzed.map((col) => {
    const minWidth = col.minWidth || 60;
    const weight = col.maxLen || 1;
    const additional = (weight / totalWeight) * extraSpace;
    return {
      ...col,
      computedWidth: Math.max(minWidth, minWidth + additional),
    };
  });
};

const buildEventsData = (results = []) => {
  const eventsMap = new Map();

  results.forEach((team) => {
    const eventId = team.eventId;
    if (!eventsMap.has(eventId)) {
      eventsMap.set(eventId, {
        eventId,
        eventName: team.eventName,
        eventData: [],
        showPrize: true,
      });
    }

    const event = eventsMap.get(eventId);
    event.showPrize = event.showPrize && Boolean(team.prize);

    let teamName = team.teamName;

    if (!team.isIndividualEvent && team.bowlers && team.bowlers.length > 0) {
      if (team.bowlers.length === 1) {
        teamName = team.bowlers[0].name;
      } else if (team.bowlers.length === 2) {
        teamName = `${team.bowlers[0].name} and ${team.bowlers[1].name}`;
      }
    } else if (team.isIndividualEvent) {
      teamName = team.bowlerName;
    }

    const sanitizedTeamName = stripPlacementRules(teamName);
    const sanitizedTieDescription = stripPlacementRules(
      team.tieDescription || ""
    );

    // Sanitize position field to remove "(placement rules)"
    const sanitizedPosition = stripPlacementRules(
      team.position ? String(team.position) : ""
    );

    event.eventData.push({
      position: sanitizedPosition,
      teamName: sanitizedTeamName,
      teamScore: team.isIndividualEvent
        ? team.individualHandicapTotal || team.individualScratchTotal
        : team.teamHandicapTotal || team.teamScratchTotal,
      prize: team.prize || 0,
      isTied: team.isTied || false,
      tieDescription: sanitizedTieDescription,
      originalPrizes: team.originalPrizes || [],
    });
  });

  eventsMap.forEach((event) => {
    event.eventData.sort((a, b) => (a.position || 999) - (b.position || 999));
  });

  return Array.from(eventsMap.values());
};

const buildTemplatePayload = async ({
  results,
  tournamentName,
  tournamentId,
  organizationName,
  isResultsFinalized,
}) => {
  const events = buildEventsData(results);
  const resultsUrl = `${SITE_ORIGIN}${tournamentPath(tournamentId, "/standings")}`;
  // Generate QR code with smaller size to reduce payload
  const qrCodeDataUrlRaw = await QRCode.toDataURL(resultsUrl, {
    width: 150,
    margin: 1,
    errorCorrectionLevel: "M", // Medium error correction for smaller size
  });
  // Compress QR code further - QR codes work well as JPEG
  const qrCodeDataUrl = await compressImage(qrCodeDataUrlRaw, 100, 0.6);
  const logoDataUrl = await loadImageAsDataUrl(logo);
  const paragraphNote = `Generated on ${new Date().toLocaleDateString()}`;
  const fileName = `${slugify(tournamentName)}-results.pdf`;

  const FOOTER_BUFFER = 30;
  const availableHeight =
    PAGE_HEIGHT - TABLE_CONFIG.topOffset - MARGINS.bottom - FOOTER_BUFFER;
  const estimateRowHeight = (row) => {
    const baseHeight = TABLE_CONFIG.rowHeight;
    if (!row || !row.length) return baseHeight;
    const prizeCell = row[row.length - 1];
    const needsTieWrap =
      isResultsFinalized &&
      prizeCell &&
      typeof prizeCell === "object" &&
      prizeCell.type === "prize" &&
      prizeCell.secondary;
    return needsTieWrap ? baseHeight + 10 : baseHeight;
  };

  const chunkRowsByHeight = (rows, maxHeight) => {
    const chunks = [];
    let currentChunk = [];
    let currentHeight = 0;

    rows.forEach((row) => {
      const rowHeightEstimate = estimateRowHeight(row);
      if (
        currentChunk.length > 0 &&
        currentHeight + rowHeightEstimate > maxHeight
      ) {
        chunks.push(currentChunk);
        currentChunk = [];
        currentHeight = 0;
      }
      currentChunk.push(row);
      currentHeight += rowHeightEstimate;
    });

    if (currentChunk.length > 0) {
      chunks.push(currentChunk);
    }

    return chunks;
  };

  const pages = [];

  if (events.length === 0) {
    pages.push({
      size: { width: PAGE_WIDTH, height: PAGE_HEIGHT },
      elements: [
        {
          type: "text",
          text: tournamentName || "Tournament Standings",
          x: MARGINS.left,
          y: 80,
          width: PAGE_WIDTH - MARGINS.left - MARGINS.right,
          fontSize: 20,
          fontWeight: "bold",
          align: "center",
        },
        {
          type: "text",
          text: "No standings available.",
          x: MARGINS.left,
          y: 140,
          width: PAGE_WIDTH - MARGINS.left - MARGINS.right,
          fontSize: 14,
          align: "center",
          color: "#555555",
        },
      ],
    });
  } else {
    events.forEach((event) => {
      const tableWidth = PAGE_WIDTH - MARGINS.left - MARGINS.right;
      const teamLabel = event.eventData.some(
        (entry) => (entry.teamName || "").length > 25
      )
        ? "Participant"
        : "Team Name";

      const columnBlueprint = [
        {
          key: "position",
          label: "Position",
          minWidth: 60,
          align: "center",
          importance: 4,
        },
        {
          key: "teamName",
          label: teamLabel,
          minWidth: 200,
          importance: 12,
        },
        {
          key: "teamScore",
          label: "Score",
          minWidth: 90,
          align: "center",
          importance: 6,
        },
      ];

      if (isResultsFinalized) {
        columnBlueprint.push({
          key: "prize",
          label: "Prize",
          minWidth: 90,
          align: "center",
          importance: 6,
        });
      }

      const dynamicColumns = computeColumnWidths(
        columnBlueprint,
        event.eventData,
        tableWidth
      );

      const baseColumns = dynamicColumns.map((col) => ({
        key: col.key,
        label: col.label,
        width: col.computedWidth,
        align: col.align || "left",
      }));

      const rows =
        event.eventData.length > 0
          ? event.eventData.map((row) => {
              const base = [
                row.position ?? "-",
                row.teamName ?? "-",
                formatScore(row.teamScore),
              ];
              if (isResultsFinalized) {
                const prizeText = row.prize ? formatCurrency(row.prize) : "-";
                const tieSuffix =
                  row.isTied && row.prize
                    ? row.tieDescription ||
                      (row.originalPrizes && row.originalPrizes.length
                        ? `(${row.originalPrizes
                            .map((p) => formatCurrency(p))
                            .join(" + ")})/${row.originalPrizes.length}`
                        : null)
                    : null;
                if (tieSuffix) {
                  base.push({
                    type: "prize",
                    primary: prizeText,
                    secondary: tieSuffix,
                  });
                } else {
                  base.push({
                    type: "prize",
                    primary: prizeText,
                  });
                }
              }
              return base;
            })
          : [
              [
                "-",
                "No participants",
                "-",
                isResultsFinalized ? "-" : null,
              ].filter(Boolean),
            ];

      const rowChunks = chunkRowsByHeight(rows, availableHeight);

      rowChunks.forEach((chunk, chunkIndex) => {
        const pageElements = [];
        const isFirstPageOfEvent = chunkIndex === 0;

        // Add QR code at the top of the first page of each event
        if (isFirstPageOfEvent && qrCodeDataUrl) {
          pageElements.push({
            type: "image",
            dataUrl: qrCodeDataUrl,
            x: MARGINS.left,
            y: 40,
            width: 60,
            height: 60,
          });
          // Add "Online Standings" label below QR code
          pageElements.push({
            type: "text",
            text: "Online Standings",
            x: MARGINS.left,
            y: 105,
            width: 60,
            fontSize: 7,
            align: "center",
            color: "#666666",
          });
        }

        const headerElements = [
          {
            type: "text",
            text: event.eventName || "Event",
            x: MARGINS.left,
            y: 40,
            width: PAGE_WIDTH - MARGINS.left - MARGINS.right,
            fontSize: 20,
            fontWeight: "bold",
            align: "center",
          },
          {
            type: "text",
            text: tournamentName || "Tournament Standings",
            x: PAGE_WIDTH - MARGINS.right - 220,
            y: 45,
            width: 220,
            align: "right",
            fontWeight: "bold",
            fontSize: 12,
          },
          {
            type: "text",
            text: isResultsFinalized
              ? "Official Standings"
              : "Unofficial Standings",
            x: PAGE_WIDTH - MARGINS.right - 220,
            y: 70,
            width: 220,
            align: "right",
            fontSize: 11,
            color: isResultsFinalized ? "#0f766e" : "#a16207",
          },
          organizationName
            ? {
                type: "text",
                text: organizationName,
                x: PAGE_WIDTH - MARGINS.right - 220,
                y: 95,
                width: 220,
                align: "right",
                fontSize: 10,
                color: "#4b5563",
              }
            : null,
        ].filter(Boolean);

        pageElements.push(...headerElements);

        pageElements.push({
          type: "table",
          x: MARGINS.left,
          y: TABLE_CONFIG.topOffset,
          width: PAGE_WIDTH - MARGINS.left - MARGINS.right,
          header: { columns: baseColumns },
          rows: chunk,
          rowHeight: TABLE_CONFIG.rowHeight,
          headerHeight: TABLE_CONFIG.headerHeight,
          zebra: true,
          footerMargin: MARGINS.bottom,
        });

        pageElements.push(
          {
            type: "text",
            text: paragraphNote,
            x: MARGINS.left,
            y: PAGE_HEIGHT - (MARGINS.bottom - 5),
            width: 240,
            fontSize: 9,
            color: "#555555",
          },
          {
            type: "text",
            text: "Powered by BowlingTournamentApp.com",
            x: PAGE_WIDTH - MARGINS.right - 240,
            y: PAGE_HEIGHT - (MARGINS.bottom - 5),
            width: 240,
            fontSize: 9,
            color: "#6b7280",
            align: "right",
          }
        );

        if (logoDataUrl) {
          pageElements.push({
            type: "image",
            dataUrl: logoDataUrl,
            x: PAGE_WIDTH / 2 - 35,
            y: PAGE_HEIGHT - (MARGINS.bottom - 10),
            width: 70,
            height: 25,
          });
        }

        pages.push({
          size: { width: PAGE_WIDTH, height: PAGE_HEIGHT },
          elements: pageElements,
        });
      });
    });
  }

  const totalPages = pages.length;

  const templatedPages = pages.map((page, index) => ({
    ...page,
    elements: [
      ...page.elements,
      {
        type: "text",
        text: `Page ${index + 1} of ${totalPages}`,
        x: MARGINS.left,
        y: PAGE_HEIGHT - (MARGINS.bottom - 35),
        fontSize: 8.5,
        color: "#6b7280",
      },
    ],
  }));

  return {
    template: { pages: templatedPages },
    metadata: { fileName, tournamentId, pageCount: templatedPages.length },
  };
};

export const useTournamentExports = (
  results = [],
  tournamentName = "",
  tournamentId = "",
  organizationName = "",
  isResultsFinalized = false
) => {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const generatePDF = useCallback(async () => {
    if (isGeneratingPDF) return;
    setIsGeneratingPDF(true);

    try {
      if (!tournamentId) {
        throw new Error("Tournament ID is required to generate PDFs.");
      }

      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error(
          "Supabase configuration is missing. Please check environment variables."
        );
      }

      const payload = await buildTemplatePayload({
        results,
        tournamentName,
        tournamentId,
        organizationName,
        isResultsFinalized,
      });

      const fetchWithRetry = async (attempt = 1) => {
        const response = await fetch(
          `${supabaseUrl}/functions/v1/generate-pdf`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${supabaseAnonKey}`,
              apikey: supabaseAnonKey,
            },
            body: JSON.stringify(payload),
          }
        );

        if (!response.ok) {
          if (
            attempt < 2 &&
            (response.status === 429 || response.status === 503)
          ) {
            await new Promise((resolve) => setTimeout(resolve, attempt * 750));
            return fetchWithRetry(attempt + 1);
          }
          const errorBody = await response.text();
          throw new Error(
            `Failed to generate PDF (${response.status}): ${errorBody}`
          );
        }

        return response;
      };

      const response = await fetchWithRetry();

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = payload.metadata.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating PDF:", error);
      notify.error("PDF generation failed. Please try again.");
    } finally {
      setIsGeneratingPDF(false);
    }
  }, [
    results,
    tournamentName,
    tournamentId,
    organizationName,
    isResultsFinalized,
    isGeneratingPDF,
  ]);

  const generateCSV = useCallback(
    (filteredResults, showPayouts) => {
      try {
        if (!filteredResults || filteredResults.length === 0) {
          throw new Error("No standings available for CSV export.");
        }

        const csvData = filteredResults.map((team) => {
          const baseData = {
            Position: team.position,
            "Team Name": team.teamName || "N/A",
            Event: team.eventName || "N/A",
          };

          if (team.isIndividualEvent) {
            baseData["Handicap Score"] =
              team.individualHandicapTotal || team.teamHandicapTotal || "N/A";
            baseData["Scratch Score"] =
              team.individualScratchTotal || team.teamScratchTotal || "N/A";
          } else {
            baseData["Handicap Score"] = team.teamHandicapTotal || "N/A";
            baseData["Scratch Score"] = team.teamScratchTotal || "N/A";
          }

          if (showPayouts && team.prize) {
            baseData.Prize = `$${team.prize.toFixed(2)}`;
          }

          if (team.bowlers && team.bowlers.length > 0) {
            baseData.Bowlers = team.bowlers.map((b) => b.name).join(", ");
          } else if (team.bowlerName) {
            baseData.Bowlers = team.bowlerName;
          } else if (team.bowlers && team.bowlers.length === 1) {
            baseData.Bowlers = team.bowlers[0].name;
          } else {
            baseData.Bowlers = "N/A";
          }

          return baseData;
        });

        const headers = Object.keys(csvData[0]);
        const csvString = [
          headers.join(","),
          ...csvData.map((row) =>
            headers
              .map((header) => {
                const value = row[header];
                if (
                  typeof value === "string" &&
                  (value.includes(",") || value.includes('"'))
                ) {
                  return `"${value.replace(/"/g, '""')}"`;
                }
                return value;
              })
              .join(",")
          ),
        ].join("\n");

        const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${tournamentName || "tournament"}-results.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error generating CSV:", error);
      }
    },
    [tournamentName]
  );

  return {
    generatePDF,
    generateCSV,
    isGeneratingPDF,
  };
};
